import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

// ── Particle canvas background ────────────────────────────────────────────
function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const dots = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 3 + 1,
      dx: (Math.random() - 0.5) * 0.5,
      dy: (Math.random() - 0.5) * 0.5,
      o: Math.random() * 0.5 + 0.2,
    }));
    function draw() {
      ctx.clearRect(0, 0, W, H);
      dots.forEach(d => {
        d.x += d.dx; d.y += d.dy;
        if (d.x < 0 || d.x > W) d.dx *= -1;
        if (d.y < 0 || d.y > H) d.dy *= -1;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(215,38,56,${d.o})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    }
    draw();
    const onResize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }} />;
}

export default function Login() {
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, password); navigate('/'); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { const data = await api.post('/auth/verify-invite', { code: inviteCode }); setInviteData(data); setMode('create-password'); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleRegisterInvite(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setLoading(true);
    try {
      const data = await api.post('/auth/register-invite', { code: inviteCode, password: newPassword });
      await loginWithToken(data.token, data.trainer);
      navigate('/');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>

      <ParticleCanvas />

      {/* Top glow */}
      <div style={{ position: 'fixed', top: -160, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(215,38,56,0.18) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 400 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src="/icons/icon-menu-logo.png" alt="Win-R" style={{ height: 64, objectFit: 'contain', marginBottom: 16 }} onError={e => e.target.style.display = 'none'} />
          <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>Win-R</div>
          <div style={{ fontSize: 14, color: '#555', marginTop: 6 }}>
            {mode === 'login' ? 'Sign in to your account' : mode === 'invite-code' ? 'Enter your invite code' : `Welcome, ${inviteData?.name}!`}
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(215,38,56,0.1)', border: '1px solid rgba(215,38,56,0.3)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <span style={{ color: '#D72638', fontSize: 13 }}>{error}</span>
          </div>
        )}

        {/* Standard login */}
        {mode === 'login' && (
          <>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required autoComplete="email"
                style={inputStyle} />
              <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required
                style={inputStyle} />
              <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 4, boxShadow: '0 4px 20px rgba(215,38,56,0.35)' }}>
                {loading ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Signing in...</> : 'Sign In'}
              </button>
            </form>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 24, fontSize: 13 }}>
              <Link to="/register" style={{ color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}>Create Account</Link>
              <button onClick={() => { setMode('invite-code'); setError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}>
                Have an invite code?
              </button>
            </div>
          </>
        )}

        {/* Invite code entry */}
        {mode === 'invite-code' && (
          <>
            <form onSubmit={handleVerifyCode} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input className="form-input" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} placeholder="e.g. A3K9BX2M" required maxLength={8}
                style={{ ...inputStyle, textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: 4, fontFamily: 'monospace' }} />
              <button className="btn btn-primary" type="submit" disabled={loading || inviteCode.length < 8} style={{ boxShadow: '0 4px 20px rgba(215,38,56,0.35)' }}>
                {loading ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Verifying...</> : 'Verify Code'}
              </button>
            </form>
            <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13 }}>
              <button onClick={() => { setMode('login'); setError(''); setInviteCode(''); }}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 13 }}>← Back to sign in</button>
            </p>
          </>
        )}

        {/* Create password */}
        {mode === 'create-password' && inviteData && (
          <>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#888', border: '1px solid rgba(255,255,255,0.08)' }}>
              Joining as <strong style={{ color: '#fff' }}>{inviteData.name}</strong> · {inviteData.email}
            </div>
            <form onSubmit={handleRegisterInvite} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Create password" required style={inputStyle} />
              <input className="form-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" required style={inputStyle} />
              <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 4, boxShadow: '0 4px 20px rgba(215,38,56,0.35)' }}>
                {loading ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Creating account...</> : "Let's Go!"}
              </button>
            </form>
          </>
        )}

      </div>
    </div>
  );
}

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12, padding: '14px 16px',
  fontSize: 15, color: '#fff',
  width: '100%', boxSizing: 'border-box',
};
